import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Building2, ChevronRight, Handshake, Search } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { MobilePage } from "../layout/MobilePage";
import { CompanyAvatar } from "./CompanyAvatar";
import type { Company } from "../types";
import { EmptyState } from "../misc/EmptyState";

// LDF: Firmen auf dem Handy – Suche, Schnellfilter „Kunden“ (Standard) und kompakte Liste.

const PAGE = 40;

export const MobileCompanyList = () => {
  const { companySectors } = useConfigurationContext();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [customersOnly, setCustomersOnly] = useState(true);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setLimit(PAGE), [term, customersOnly]);

  const { data, isPending } = useQuery({
    queryKey: ["mobile-companies", term, customersOnly, limit],
    queryFn: async () => {
      let q = getSupabaseClient()
        .from("companies_summary")
        .select("id,name,sector,city,district,logo,nb_deals,contact_blocked", { count: "exact" })
        .order("name")
        .limit(limit);
      // Suche findet alles (auch gesperrte, ausgeblendete, private); ohne Suche nur die Standardansicht
      if (term) q = q.ilike("name", `%${term.replace(/[%,()]/g, " ")}%`);
      else q = q.eq("is_private", false).eq("hidden", false).eq("contact_blocked", false);
      if (customersOnly && !term) q = q.eq("is_customer", true);
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as Company[], count: count ?? 0 };
    },
  });

  return (
    <MobilePage title="Firmen">
      <div className="mb-3 flex gap-2">
        <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg border border-input bg-background px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Firma suchen …"
            className="min-w-0 flex-1 bg-transparent text-base outline-none"
          />
        </label>
        <button
          type="button"
          onClick={() => setCustomersOnly(!customersOnly)}
          className={`inline-flex h-11 shrink-0 items-center gap-1 rounded-lg px-3 text-sm cursor-pointer ${customersOnly && !term ? "bg-blue-600 text-white" : "border border-border text-blue-600"}`}
          aria-pressed={customersOnly}
        >
          <Handshake className="h-4 w-4" /> Kunden
        </button>
      </div>
      {term && <p className="mb-2 text-xs text-muted-foreground">Suche in allen Firmen (auch privat, intern und gesperrt).</p>}

      {isPending ? (
        <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
      ) : !data?.rows.length ? (
        <EmptyState Icon={Building2} title="Keine Firmen gefunden" text={customersOnly && !term ? "Tipp: „Kunden“ ausschalten oder suchen." : undefined} />
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {data.rows.map((c) => (
              <li key={c.id}>
                <Link to={`/companies/${c.id}/show`} className="flex items-center gap-3 px-3 py-3 no-underline">
                  <CompanyAvatar record={c} />
                  <div className="min-w-0 flex-1">
                    <div className={`truncate font-medium ${c.nb_deals ? "text-blue-600 dark:text-blue-400" : ""}`}>{c.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[companySectors.find((s) => s.value === c.sector)?.label, c.district || c.city].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
          {data.count > data.rows.length && (
            <button type="button" onClick={() => setLimit(limit + PAGE)} className="mt-3 h-11 w-full rounded-lg border border-border text-sm cursor-pointer">
              Weitere laden ({data.count - data.rows.length})
            </button>
          )}
        </>
      )}
    </MobilePage>
  );
};
